import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Regalo automático por monto de carrito.
 *
 * Cuando el subtotal del carrito (sin contar el regalo) alcanza el monto
 * configurado, se agrega automáticamente el producto de regalo. Si el
 * subtotal baja del monto, el producto se elimina del carrito, incluso si
 * el cliente lo agregó manualmente: este producto solo puede estar en el
 * carrito cuando el ticket cumple el monto mínimo, y siempre con 1 unidad.
 *
 * Config: JSON en <script type="application/json" id="auto-gift-config">
 * generado por snippets/auto-gift.liquid.
 */

const SOURCE_ID = 'auto-gift';
const GIFT_PROPERTY = '_auto_gift';
const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)/;

const configEl = document.getElementById('auto-gift-config');
const config = configEl ? JSON.parse(configEl.textContent || '{}') : null;

if (config && config.variantId && config.thresholdCents > 0) {
  init(config);
}

function init(config) {
  const routes = (window.Theme && window.Theme.routes) || {};
  const cartUrl = `${routes.cart_url || '/cart'}.js`;
  const addUrl = routes.cart_add_url || '/cart/add.js';
  const changeUrl = `${routes.cart_change_url || '/cart/change'}.js`;

  // fetch original, para que nuestras propias llamadas no re-disparen el interceptor
  const nativeFetch = window.fetch.bind(window);

  let syncing = false;
  let pendingSync = false;
  let debounceTimer = null;
  let addBlockedUntil = 0; // evita reintentos en bucle si el add falla (ej. sin stock)

  /** Programa una sincronización con un pequeño debounce. */
  function scheduleSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(sync, 350);
  }

  /** Revisa el carrito y agrega o elimina el regalo según el subtotal. */
  async function sync() {
    if (syncing) {
      pendingSync = true;
      return;
    }
    syncing = true;

    try {
      const cart = await fetchCart();
      if (!cart) return;

      // Cualquier línea del producto de regalo cuenta, la haya agregado
      // el cliente manualmente o esta función de forma automática
      const giftLines = cart.items.filter((item) => item.product_id === config.productId);

      // Subtotal sin contar las líneas del regalo
      const giftTotal = giftLines.reduce((sum, item) => sum + item.final_line_price, 0);
      const subtotal = cart.items_subtotal_price - giftTotal;

      const qualifies = subtotal >= config.thresholdCents;

      if (qualifies) {
        if (giftLines.length === 0) {
          if (config.available && Date.now() > addBlockedUntil) {
            await addGift();
          }
        } else {
          // Mantener una sola línea del regalo con exactamente 1 unidad
          if (giftLines[0].quantity !== 1) {
            await changeGift(giftLines[0].key, 1);
          }
          for (const extraLine of giftLines.slice(1)) {
            await changeGift(extraLine.key, 0);
          }
        }
      } else {
        // Ticket insuficiente: el producto no puede quedarse en el carrito
        for (const line of giftLines) {
          await changeGift(line.key, 0);
        }
      }
    } catch (error) {
      console.error('[auto-gift] error al sincronizar el carrito', error);
    } finally {
      syncing = false;
      if (pendingSync) {
        pendingSync = false;
        scheduleSync();
      }
    }
  }

  async function fetchCart() {
    const response = await nativeFetch(cartUrl, { cache: 'no-store' });
    if (!response.ok) return null;
    return response.json();
  }

  async function addGift() {
    const response = await nativeFetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: config.variantId,
            quantity: 1,
            properties: { [GIFT_PROPERTY]: 'true' },
          },
        ],
      }),
    });

    if (!response.ok) {
      addBlockedUntil = Date.now() + 60000;
      return;
    }

    await notifyThemeOfUpdate();
    scheduleSync();
  }

  async function changeGift(lineKey, quantity) {
    const response = await nativeFetch(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity }),
    });

    if (!response.ok) return;

    await notifyThemeOfUpdate();
  }

  /** Notifica a los componentes del tema (drawer, burbuja, página de carrito) para que se re-rendericen. */
  async function notifyThemeOfUpdate() {
    const cart = await fetchCart();
    document.dispatchEvent(
      new CartUpdateEvent({}, SOURCE_ID, {
        source: SOURCE_ID,
        itemCount: cart ? cart.item_count : 0,
      })
    );
  }

  // 1. Eventos del propio tema (agregar al carrito, cambiar cantidades, etc.)
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.source === SOURCE_ID) return;
    scheduleSync();
  });

  // 2. Interceptar fetch: cubre apps de carrito externas (ej. Corner) que no emiten eventos del tema
  window.fetch = function (input, ...rest) {
    const result = nativeFetch(input, ...rest);
    try {
      const url = typeof input === 'string' ? input : input?.url || '';
      if (CART_MUTATION_PATTERN.test(url)) {
        result.then((response) => {
          if (response.ok) scheduleSync();
        });
      }
    } catch (_) {
      // no-op
    }
    return result;
  };

  // 3. Interceptar XMLHttpRequest por si alguna app lo usa en lugar de fetch
  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (typeof url === 'string' && CART_MUTATION_PATTERN.test(url)) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) scheduleSync();
      });
    }
    return nativeOpen.call(this, method, url, ...rest);
  };

  // 4. Revisión inicial al cargar la página
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync);
  } else {
    scheduleSync();
  }
}
